import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { StoreApi } from '../api/storeApi';
import { SsoMicrosoftApi } from '../api/ssoMicrosoftApi';
import { setSuccessNotification, setErrorNotification } from '../../../actions/notificationAction';
import useTranslation from '../../../hooks/useTranslation';
import { AuthSettings, SsoSettings } from '../../../shared-components';

const INITIAL_STORE = {
  enable_account_registration: false,
  auto_account_approval: false,
  password_protection: 0,
  authentication: 0
};

const AuthSettingsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: storeId } = useParams();

  const [store, setStore] = useState(INITIAL_STORE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const notifyError = useCallback(
    (e) => {
      dispatch(setErrorNotification({ message: (e && e.response && e.response.data && e.response.data.message) || 'Something went wrong' }));
    },
    [dispatch]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await StoreApi.getStore(storeId);
      setStore({
        ...data,
        password_protection: Number(data.password_protection) || 0,
        authentication: Number(data.authentication) || 0
      });
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  }, [notifyError, storeId]);

  useEffect(() => {
    load();

    const url = new URL(window.location.href);
    const microsoftStatus = url.searchParams.get('microsoft');
    if (microsoftStatus === 'connected') {
      dispatch(setSuccessNotification({ message: 'Microsoft SSO Connected!' }));
    } else if (microsoftStatus === 'failed') {
      dispatch(setErrorNotification({ message: 'Microsoft SSO Failed.' }));
    }
    if (microsoftStatus) {
      url.searchParams.delete('microsoft');
      window.history.replaceState({}, document.title, url.toString());
    }
  }, [load]);

  const onChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;

    const updatedValue =
      type === 'checkbox'
        ? checked
        : (name === 'authentication' || name === 'password_protection')
          ? Number(value)
          : value;

    setStore((prev) => {
      const updatedStore = { ...prev, [name]: updatedValue };
      if (name === 'authentication' && (Number(value) === 0 || Number(value) === 3)) {
        updatedStore.enable_account_registration = false;
      }
      return updatedStore;
    });
  }, []);

  const onSave = useCallback(async () => {
    try {
      setSaving(true);
      await StoreApi.updateStore(storeId, store);
      dispatch(setSuccessNotification({ message: t('response_messages.store_updated_successfully') }));
    } catch (e) {
      notifyError(e);
    } finally {
      setSaving(false);
    }
  }, [dispatch, notifyError, store, storeId, t]);

  const handleSsoConnect = useCallback(async () => {
    try {
      const { data } = await SsoMicrosoftApi.getSecureUrl(storeId);
      if (!data?.token) throw new Error('Missing Microsoft SSO token');
      const url = `${process.env.REACT_APP_STORE_URL}/auth/sso/microsoft/connect?token=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      notifyError(e);
    }
  }, [notifyError, storeId]);

  const handleSsoDisconnect = useCallback(async (id) => {
    try {
      await SsoMicrosoftApi.disconnectMicrosoft(id, storeId);
      dispatch(setSuccessNotification({ message: 'Microsoft SSO disconnected successfully' }));

      if (store.authentication === 3) {
        await StoreApi.updateStore(storeId, { ...store, authentication: 2 });
      }

      await load();
    } catch (e) {
      notifyError(e);
      throw e;
    }
  }, [dispatch, load, notifyError, store, storeId]);

  const handleSsoToggleEnabled = useCallback(async (config) => {
    try {
      await SsoMicrosoftApi.toggleMicrosoftSso(config, storeId);
      dispatch(setSuccessNotification({ message: `Microsoft SSO ${config.enabled ? 'enabled' : 'disabled'} successfully` }));

      if (!config.enabled && store.authentication === 3) {
        await StoreApi.updateStore(storeId, { ...store, authentication: 2 });
      }

      await load();
    } catch (e) {
      notifyError(e);
      throw e;
    }
  }, [dispatch, load, notifyError, store, storeId]);

  const micConfig = (store.ssoConfigs || []).find(function(c) { return c.provider === 'microsoft' && c.tenant_id; });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthSettings
      store={store}
      onChange={onChange}
      onSave={onSave}
      saving={saving}
      ssoContent={
        <SsoSettings
          micConfig={micConfig}
          onConnect={handleSsoConnect}
          onDisconnect={handleSsoDisconnect}
          onToggleEnabled={handleSsoToggleEnabled}
        />
      }
    />
  );
};

export default AuthSettingsContainer;
